import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { message } from 'antd';
import { useAuthStore, type AuthUser } from '../store/authStore';

const AUTH_BASE = 'https://usuariospipefy.grupomultiluz.com.br/api/auth';

export default function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const login = useAuthStore((s) => s.login);

  const [mode, setMode] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sucessoMsg, setSucessoMsg] = useState('');

  const onLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !senha) {
      message.error('Informe email e senha');
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${AUTH_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // credentials: 'include' é OBRIGATÓRIO pro browser guardar o cookie
        // mtz_auth (.grupomultiluz.com.br) setado pela resposta cross-origin.
        // Sem isso, o token funciona em sessionStorage mas o iframe Pipefy
        // não tem como ler nada — fica eternamente em "faça login".
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase(), senha }),
      });
      const data = (await r.json().catch(() => ({}))) as { token?: string; usuario?: AuthUser; erro?: string };
      if (!r.ok || !data.token || !data.usuario) {
        message.error(data.erro || 'Credenciais inválidas');
        return;
      }
      login(data.token, data.usuario);
      const next = params.get('next');
      navigate(next ? decodeURIComponent(next) : '/', { replace: true });
    } catch (err) {
      console.error(err);
      message.error('Falha ao conectar com o servidor de auth');
    } finally {
      setSubmitting(false);
    }
  };

  const onForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      message.error('Informe o e-mail');
      return;
    }
    setSubmitting(true);
    setSucessoMsg('');
    try {
      const r = await fetch(`${AUTH_BASE}/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const data = (await r.json().catch(() => ({}))) as { mensagem?: string; erro?: string };
      if (!r.ok) {
        message.error(data.erro || 'Falha ao solicitar nova senha');
        return;
      }
      setSucessoMsg(data.mensagem || 'Se o e-mail estiver cadastrado, uma nova senha foi enviada.');
    } catch (err) {
      console.error(err);
      message.error('Falha ao conectar com o servidor de auth');
    } finally {
      setSubmitting(false);
    }
  };

  const ehLogin = mode === 'login';

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <form
        onSubmit={ehLogin ? onLogin : onForgot}
        style={{ background: '#fff', borderRadius: 12, padding: '32px 28px', width: '100%', maxWidth: 380, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}
      >
        <p style={{ fontSize: 12, color: '#999', textTransform: 'uppercase', letterSpacing: 1, margin: 0 }}>Engenharia</p>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111', margin: '4px 0 24px' }}>
          {ehLogin ? 'Entrar' : 'Recuperar senha'}
        </h1>

        {!ehLogin && (
          <p style={{ fontSize: 13, color: '#666', marginTop: -8, marginBottom: 16 }}>
            Informe seu e-mail Multiluz. Vamos enviar uma nova senha para ele.
          </p>
        )}

        <label style={{ display: 'block', fontSize: 12, color: '#444', fontWeight: 600, marginBottom: 6 }}>E-mail</label>
        <input
          type="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: 8, fontSize: 14, marginBottom: 16, outline: 'none' }}
          placeholder="seu.email@grupomultiluz.com.br"
          required
        />

        {ehLogin && (
          <>
            <label style={{ display: 'block', fontSize: 12, color: '#444', fontWeight: 600, marginBottom: 6 }}>Senha</label>
            <input
              type="password"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e5e5', borderRadius: 8, fontSize: 14, marginBottom: 20, outline: 'none' }}
              required
            />
          </>
        )}

        <button
          type="submit"
          disabled={submitting}
          style={{ width: '100%', padding: '11px 16px', background: '#111', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: submitting ? 'wait' : 'pointer', opacity: submitting ? 0.7 : 1 }}
        >
          {submitting
            ? (ehLogin ? 'Entrando…' : 'Enviando…')
            : (ehLogin ? 'Entrar' : 'Enviar nova senha')}
        </button>

        {sucessoMsg && (
          <p style={{ fontSize: 12, color: '#15803d', textAlign: 'center', marginTop: 14, marginBottom: 0, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '8px 10px', borderRadius: 8 }}>
            {sucessoMsg}
          </p>
        )}

        <button
          type="button"
          onClick={() => { setMode(ehLogin ? 'forgot' : 'login'); setSucessoMsg(''); }}
          style={{ display: 'block', width: '100%', background: 'transparent', border: 'none', color: '#666', fontSize: 12, marginTop: 16, cursor: 'pointer', textDecoration: 'underline' }}
        >
          {ehLogin ? 'Esqueci minha senha' : '← Voltar pro login'}
        </button>

        <p style={{ fontSize: 11, color: '#999', textAlign: 'center', marginTop: 16, marginBottom: 0 }}>
          Autenticação centralizada · usuariospipefy
        </p>
      </form>
    </div>
  );
}
